import React, { useState, useEffect } from "react";
import UploadModal from "../modal/UploadModal";
import { ArrowUpTrayIcon, PhotoIcon, PencilIcon, ClipboardIcon, CheckIcon } from "@heroicons/react/24/outline";
import { assetsApi } from "../../repositories/assets";
import Swal from 'sweetalert2';
interface Asset {
  id: number;
  userId: number;
  artistId: string;
  url: string;
  imageTitle: string;
  createdAt: string;
  updatedAt: string;
}

interface AssetsTabProps {
  artistId: string;
}

const AssetsTab: React.FC<AssetsTabProps> = ({ artistId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [updating, setUpdating] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  useEffect(() => {
    if (artistId) {
      loadAssets();
    }
  }, [artistId]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await assetsApi.getByArtistId(artistId);
      setAssets(response.data?.data.images || []);
    } catch (error) {
      console.error('Error loading artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (newAssets: Asset[]) => {
    setAssets(prevAssets => [...prevAssets, ...newAssets]);
  };

  const startEditing = (index: number, asset: Asset) => {

    setEditingId(index);
    setEditTitle(asset.imageTitle);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const handleCopyUrl = async (url: string, index: number) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(index);
      
      // Show success message with Swal
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Image link copied!',
        showConfirmButton: false,
        timer: 1500,
        position: 'top-end',
        toast: true
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to copy URL',
      });
    }
  };

  const handleUpdateTitle = async (artistId: string, index: number, newTitle: string) => {
    setUpdating(true);
    try {

      if (!newTitle.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Title cannot be empty.',
        });
        return;
      }
      await assetsApi.update(artistId, index, newTitle);
      setAssets(prev =>
        prev.map((a, i) =>
          i === index ? { ...a, imageTitle: newTitle } : a
        )
      );
      setEditingId(null);
      setEditTitle("");
    } catch (err) {
      alert("Update failed");
    } finally {
      setUpdating(false);
    }
  };
  return (
    <div className="p-6 space-y-6">
      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-500">Loading assets...</p>
        </div>
      ) : assets.length > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Artist Assets</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage photos and other assets for this artist
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <ArrowUpTrayIcon className="w-5 h-5 mr-2 inline" />
              Upload More Assets
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">

            
            {assets.map((asset, index) => (
              <div key={index} className="rounded-lg overflow-hidden border border-gray-200">
                <div className="aspect-square bg-gray-100 relative group">
                  <img
                    src={asset.url}
                    alt={asset.imageTitle}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 ">
                    <button
                      onClick={() => handleCopyUrl(asset.url, index)}
                      className="p-1.5 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all shadow-sm"
                      title="Copy image URL"
                    >
                      {copiedId === index ? (
                        <CheckIcon className="w-4 h-4 text-green-600" />
                      ) : (
                        <ClipboardIcon className="w-4 h-4 text-gray-600 hover:text-gray-800" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="p-1 ">
                  {editingId === index ? (
                    <div>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        className="border px-2 py-1 rounded text-sm w-full mb-3"
                        placeholder="Enter new title"
                      />
                      <div className="flex gap-2 justify-end mr-2">
                        <button
                          onClick={cancelEditing}
                          className="text-red-700"
                          title="Cancel"
                        >
                          cancel
                        </button>
                        <button
                          onClick={() => handleUpdateTitle(artistId, index, editTitle)}
                          disabled={updating}
                          className="  text-blue-600 rounded flex items-center justify-center p-1"
                          title="Save">
                          save
                        </button>

                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between h-fit p-2">
                      <p className="font-medium text-sm truncate">{asset.imageTitle}</p>
                      <button
                        onClick={() => startEditing(index, asset)}
                        className="text-indigo-600"
                        title="Edit Title"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Artist Assets</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage photos and other assets for this artist
              </p>
            </div>
          </div>
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <PhotoIcon className="w-12 h-12 text-gray-400" />
            </div>
            <p className="text-gray-500 mb-6">
              Upload photos and other assets for this artist.
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <ArrowUpTrayIcon className="w-5 h-5 mr-2 inline" />
              Upload Asset
            </button>
          </div>
        </>
      )}

      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        artistId={artistId}
        onUploadSuccess={handleUploadSuccess}
        onSelectionChange={() => { }}
        showCheckboxes={false}
        initialSelectedImages={[]}
      />
    </div>
  );
};

export default AssetsTab;
